import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicTranslationService } from '@gs-2.0/core';

@Component({
  selector: 'app-inline-translator',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <span class="inline-translator" [class.editing]="isEditing">
      <span
        class="translation-text"
        *ngIf="!isEditing"
        (dblclick)="startEditing()"
        [title]="'Double click to edit translation for: ' + translationKey">
        {{ displayValue }}
        <button
          *ngIf="showEditButton"
          class="edit-btn"
          (click)="startEditing()"
          title="Edit translation">
          <i class="bi bi-pencil"></i>
        </button>
      </span>

      <span class="edit-wrapper" *ngIf="isEditing">
        <input
          #editInput
          type="text"
          class="edit-input"
          [(ngModel)]="editValue"
          (keyup.enter)="saveTranslation()"
          (keyup.escape)="cancelEdit()"
          (blur)="onBlur()">
        <button
          class="save-btn"
          (click)="saveTranslation()"
          title="Save">
          <i class="bi bi-check"></i>
        </button>
        <button
          class="cancel-btn"
          (click)="cancelEdit()"
          title="Cancel">
          <i class="bi bi-x"></i>
        </button>
      </span>
    </span>
  `,
  styles: [`
    .inline-translator {
      position: relative;
      display: inline-block;

      &.editing {
        display: inline-flex;
        align-items: center;
      }

      .translation-text {
        position: relative;
        cursor: text;
        padding: 2px 4px;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background-color: rgba(0, 123, 255, 0.05);

          .edit-btn {
            opacity: 1;
          }
        }

        .edit-btn {
          opacity: 0;
          margin-left: 8px;
          padding: 2px 6px;
          font-size: 12px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          transition: opacity 0.2s;

          &:hover {
            background: #0056b3;
          }

          i {
            font-size: 10px;
          }
        }
      }

      .edit-wrapper {
        display: inline-flex;
        align-items: center;
        gap: 4px;

        .edit-input {
          padding: 4px 8px;
          border: 2px solid #007bff;
          border-radius: 4px;
          font-size: inherit;
          font-family: inherit;
          min-width: 200px;
          outline: none;
        }

        .save-btn,
        .cancel-btn {
          padding: 4px 8px;
          font-size: 14px;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          transition: background-color 0.2s;

          i {
            font-size: 14px;
          }
        }

        .save-btn {
          background: #28a745;
          color: white;

          &:hover {
            background: #218838;
          }
        }

        .cancel-btn {
          background: #6c757d;
          color: white;

          &:hover {
            background: #5a6268;
          }
        }
      }
    }
  `]
})
export class InlineTranslatorComponent {
  @Input() translationKey!: string;
  @Input() defaultValue: string = '';
  @Input() showEditButton: boolean = false;
  @Input() allowEdit: boolean = true;
  @Output() translationUpdated = new EventEmitter<{ key: string; value: string }>();

  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement>;

  isEditing: boolean = false;
  editValue: string = '';
  displayValue: string = '';

  constructor(private translationService: DynamicTranslationService) {}

  ngOnInit(): void {
    this.loadTranslation();
  }

  ngOnChanges(): void {
    this.loadTranslation();
  }

  private loadTranslation(): void {
    this.translationService.translate(this.translationKey).subscribe(
      translation => {
        this.displayValue = translation || this.defaultValue || this.translationKey;
      }
    );
  }

  startEditing(): void {
    if (!this.allowEdit) return;

    this.isEditing = true;
    this.editValue = this.displayValue;

    setTimeout(() => {
      if (this.editInput) {
        this.editInput.nativeElement.focus();
        this.editInput.nativeElement.select();
      }
    });
  }

  saveTranslation(): void {
    if (this.editValue.trim() && this.editValue !== this.displayValue) {
      this.translationService.updateTranslation(this.translationKey, this.editValue).subscribe(
        () => {
          this.displayValue = this.editValue;
          this.translationUpdated.emit({
            key: this.translationKey,
            value: this.editValue
          });
          this.isEditing = false;
        },
        error => {
          console.error('Error saving translation:', error);
          this.cancelEdit();
        }
      );
    } else {
      this.cancelEdit();
    }
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.editValue = '';
  }

  onBlur(): void {
    // Small delay to allow button clicks to register
    setTimeout(() => {
      if (this.isEditing) {
        this.cancelEdit();
      }
    }, 200);
  }
}